import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from '@angular/core';
import { Router } from '@angular/router';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  TOP_BAR_MODULES,
  TopBarModule,
  TopBarScreen,
} from '../../../../configs/top-bar-navigation.config';
import { NavigationStateService } from '../../../../core/services/navigation/navigation-state.service';
import { PinnedModulesService } from '../../../../core/services/navigation/pinned-modules.service';
import { RoleService } from '../../../../core/services/role/role.service';
import { RtoAccessService } from '../../../../core/services/rto/rto-access.service';
import { TranslatePipe } from '../../../pipes/translate/translate.pipe';
import { SharedPopoverComponent } from '../../popover/popover.component';
import { ModuleIconComponent } from '../module-icon/module-icon.component';

/**
 * PinnedModulesComponent component.
 * Provides UI presentation and event handling for pinned modules component.
 */
@Component({
  selector: 'app-pinned-modules',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatTooltipModule,
    ModuleIconComponent,
    SharedPopoverComponent,
    TranslatePipe,
  ],
  templateUrl: './pinned-modules.component.html',
  styleUrl: './pinned-modules.component.scss',
})
export class PinnedModulesComponent {
  /** Injected service for pinned service. */
  readonly pinnedService = inject(PinnedModulesService);
  /** Injected service for navigation state. */
  readonly navigationState = inject(NavigationStateService);
  /** Injected service for router. */
  private readonly router = inject(Router);
  /** Injected service for role. */
  private readonly role = inject(RoleService);
  /** Injected service for rto access service. */
  private readonly rtoAccessService = inject(RtoAccessService);

  /** Module whose screen menu is currently open */
  readonly openMenuModuleKey = signal<string | null>(null);

  /** The popover instance that is currently open */
  private activePopover: SharedPopoverComponent | null = null;

  /** One pin button per module (screens open in the dropdown). */
  readonly uniqueVisiblePins = computed(() => {
    const seen = new Set<string>();
    return this.pinnedService.visiblePins().filter((pin) => {
      if (seen.has(pin.moduleKey)) return false;
      seen.add(pin.moduleKey);
      return true;
    });
  });

  /**
   * Handles get module logic.
   * @param moduleKey - The module key
   */
  getModule(moduleKey: string): TopBarModule | undefined {
    return TOP_BAR_MODULES.find((m) => m.key === moduleKey);
  }

  /**
   * Handles get module screens logic.
   * @param moduleKey - The module key
   */
  getModuleScreens(moduleKey: string): TopBarScreen[] {
    return this.getModule(moduleKey)?.screens ?? [];
  }

  /**
   * Handles get module label logic.
   * @param moduleKey - The module key
   */
  getModuleLabel(moduleKey: string): string {
    return this.getModule(moduleKey)?.name ?? moduleKey;
  }

  /** Open this pin's dropdown — close any other open pin menu first. */
  onPinClick(
    event: Event,
    moduleKey: string,
    popover: SharedPopoverComponent
  ): void {
    event.stopPropagation();
    const target = event.currentTarget as HTMLElement;

    if (
      this.openMenuModuleKey() === moduleKey &&
      this.activePopover === popover
    ) {
      popover.hide();
      return;
    }

    if (this.activePopover && this.activePopover !== popover) {
      this.activePopover.hide();
    }

    this.openMenuModuleKey.set(moduleKey);
    this.activePopover = popover;
    popover.show(event, target);
  }

  /** Navigate after a screen is chosen from the dropdown. */
  onScreenSelect(screen: TopBarScreen, popover: SharedPopoverComponent): void {
    popover.hide();
    if (
      !screen.route.startsWith('/dashboard') &&
      (screen.route.startsWith('/rto') || screen.route.includes('rto')) &&
      !this.role.isRtoEnabledForCurrentUser()
    ) {
      this.rtoAccessService.openRtoNotConfiguredModal();
      return;
    }
    this.router.navigate([screen.route], {
      queryParams: screen.queryParams ?? {},
    });
  }

  /**
   * Handles on pin popover hide logic.
   * @param moduleKey - The module key
   */
  onPinPopoverHide(moduleKey: string): void {
    if (this.openMenuModuleKey() !== moduleKey) {
      return;
    }
    this.openMenuModuleKey.set(null);
    this.activePopover = null;
  }
}
